import { useMemo } from 'react';

import { format } from 'date-fns-tz';
import addHours from 'date-fns/addHours';
import { useIntl } from 'react-intl-next';

import { useResponderNames } from '@atlassian/eoc-responder-names';
import {
	conversion,
	filterNoOne,
	type ResponderType,
	useTimezoneOptions,
} from '@atlassian/eoc-schedule-common';

import { type ActiveOverride, type RotationBase } from '../../../common/types';
import { OverrideVariant } from '../../../controllers/page-store/state/types';
import {
	useTimelineEpoch,
	useTimelineRotations,
} from '../../../controllers/schedule-store/helpers';
import { useCurrentUser } from '../../../utils/hooks/current-user';
import { getNoOne } from '../../../utils/responder';
import { isEmpty } from '../../../utils/validators';

import { type FormFields, type UseDefaultValuesParams } from './types';

const formatDateForProp = (epoch: number) => format(new Date(epoch), 'yyyy-MM-dd');

const convertRotationsToOptions = (rotations?: RotationBase[]) =>
	(rotations ?? []).map(({ id, name }) => ({
		label: name,
		value: id,
	}));

const useDefaultValues = ({ variant, base, schedule }: UseDefaultValuesParams) => {
	const { getTimezoneOption } = useTimezoneOptions();
	const [{ nowEpoch, nowOffset }] = useTimelineEpoch();
	const [rotations] = useTimelineRotations();

	const defaults = useMemo((): Pick<FormFields, 'start' | 'end' | 'rotation' | 'timezone'> => {
		const timezone = getTimezoneOption({ timeZone: schedule.timezone });

		switch (variant) {
			case OverrideVariant.Add: {
				return {
					start: conversion.toLocal(nowEpoch, nowOffset).toISOString(),
					end: conversion.toLocal(addHours(nowEpoch, 1).valueOf(), nowOffset).toISOString(),
					rotation: [],
					timezone,
				};
			}
			case OverrideVariant.Edit:
			case OverrideVariant.Customize: {
				const options = convertRotationsToOptions(rotations);

				return {
					start: conversion.toLocal(base!.startEpoch, base!.startOffset).toISOString(),
					end: conversion.toLocal(base!.endEpoch, base!.endOffset).toISOString(),
					rotation: base!.rotations.map(({ id, name }) => ({
						value: id,
						label: name ?? options.find(({ value }) => id === value)!.label,
					})),
					timezone,
				};
			}
		}
	}, [base, getTimezoneOption, nowEpoch, nowOffset, rotations, schedule.timezone, variant]);

	return defaults;
};

const useDefaultUser = ({
	variant,
	base,
}: {
	variant: OverrideVariant;
	base?: ActiveOverride<false, false>;
}) => {
	const intlApi = useIntl();
	const current = useCurrentUser();

	// NOTE: baseUser can be undefined in Customize and Add
	const [baseUser] = useResponderNames(base?.responder ? filterNoOne([base.responder]) : []);

	const initial = useMemo(
		() => ({
			isLoading: baseUser?.isLoading ?? false,
			isError: baseUser?.isError ?? false,
			user:
				isEmpty(baseUser) || baseUser?.isError || baseUser?.isLoading
					? undefined
					: {
							value: baseUser.id,
							label: baseUser.name,
							atlassianIdentifier: baseUser.atlassianIdentifier!,
							type: 'user' as ResponderType<false>,
						},
		}),
		[baseUser],
	);

	const isNoOne = base?.responder?.type === 'noone';
	const noOne = useMemo(() => {
		const [, noBody] = getNoOne(intlApi);

		return {
			isLoading: false,
			isError: false,
			user: noBody,
		};
	}, [intlApi]);

	switch (variant) {
		case OverrideVariant.Add:
			return current;

		case OverrideVariant.Customize:
			if (isEmpty(base?.responder)) {
				return current;
			} else if (isNoOne) {
				return noOne;
			} else {
				return initial;
			}

		case OverrideVariant.Edit:
			if (isNoOne) {
				return noOne;
			} else {
				return initial;
			}
	}
};

export { useDefaultValues, useDefaultUser, formatDateForProp, convertRotationsToOptions };
